import { createTheme } from '@mui/material/styles';
import { pickersFilledInputClasses } from '@mui/x-date-pickers/PickersTextField';
import { pickersSectionListClasses } from '@mui/x-date-pickers/PickersSectionList';

import { colors } from './colors';
import { gradients } from './gradients';

const inputPadding = '10px 12px 10px 12px';
const filledInputPadding = '10px 12px';

const optionStyles = {
  padding: '8px 12px',
  '&:hover': {
    backgroundColor: `${colors.blue.primary}0E`,
  },
  '&.Mui-selected': {
    backgroundColor: `${colors.blue.primary}1F`,
    '&:hover': {
      backgroundColor: `${colors.blue.primary}0E`,
    },
  },
};

const theme = createTheme({
  cssVariables: true,
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  palette: {
    ...colors,
    primary: {
      main: colors.blue.primary,
    },
    text: {
      primary: colors.black.primary,
    },
  },
  typography: {
    fontFamily: 'Jost',
    body2: {
      fontSize: 'var(--body-size)',
      lineHeight: 'var(--body-line)',
    },
    body1: {
      fontSize: 'var(--body-size)',
      lineHeight: 'var(--body-line)',
    },
    caption: {
      fontSize: 'var(--caption-size)',
      lineHeight: 'var(--caption-line)',
    },
    h2: {
      fontWeight: 700,
      fontSize: 'var(--h2-size)',
      lineHeight: 'var(--h2-line)',
    },
    h1: {
      fontWeight: 700,
      fontSize: 'var(--h1-size)',
      lineHeight: 'var(--h1-line)',
    },
    h3: {
      fontWeight: 700,
      fontSize: 'var(--h3-size)',
      lineHeight: 'var(--h3-line)',
    },
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: optionStyles,
      },
    },
    MuiButton: {
      styleOverrides: {
        startIcon: {
          marginLeft: 0,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          right: 12,
          top: 'calc(50% - 10px)',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&.MuiMenu-list': {
            padding: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        list: {
          gap: 24,
        },
        indicator: {
          height: '4px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            padding: inputPadding,
          },
        },
      },
    },
    MuiPickerDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:focus': {
            background: colors.blue.primary,
          },
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          '& .MuiYearCalendar-button.Mui-selected, & .MuiYearCalendar-button.Mui-selected:focus':
            {
              background: colors.blue.primary,
            },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiMenu-paper': {
            marginTop: '8px',
            boxShadow: 'none',
            borderRadius: '16px',
            border: `1.5px solid ${colors.blue.primary}`,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          marginTop: '4px',
          fontSize: 'var(--body-size)',
          lineHeight: 'var(--body-line)',
          '&.Mui-error': {
            color: colors.red.primary,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          marginBottom: '2px',
          fontSize: 'var(--caption-size)',
          color: colors.gray.dark.primary,
          lineHeight: 'var(--caption-line)',
          '&.Mui-error': {
            color: colors.gray.dark.primary,
          },
          '&.Mui-focused': {
            color: colors.gray.dark.primary,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          padding: 0,
          borderRadius: '12px',
        },
        input: {
          fontWeight: 400,
          padding: inputPadding,
          fontSize: 'var(--body-size)',
          lineHeight: 'var(--body-line)',
          backgroundColor: colors.white.primary,
          '&.MuiSelect-select': {
            paddingRight: '48px !important',
          },
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '8px 0 12px',
          textTransform: 'none',
          fontSize: 'var(--h2-size)',
          lineHeight: 'var(--h2-line)',
          color: colors.gray.dark.active,
          fontWeight: 'var(--h2-weight)',

          '&:hover': {
            color: colors.blue.primary,
          },

          '&.Mui-selected': {
            color: colors.blue.primary,
          },

          [theme.breakpoints.up('md')]: {
            fontSize: 'var(--h1-size)',
            lineHeight: 'var(--h1-line)',
            fontWeight: 'var(--h1-weight)',
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.MuiInputBase-root': {
            borderRadius: '16px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1.5px',
              borderColor: colors.gray.primary,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1.5px',
              borderColor: colors.blue.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1.5px',
              borderColor: colors.blue.primary,
            },
            '& input:-webkit-autofill': {
              WebkitTextFillColor: colors.black.primary,
              WebkitBoxShadow: `0 0 0 1000px ${colors.white.primary} inset`,
            },
          },
        },
      },
    },
    MuiPickersFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          backgroundColor: colors.white.primary,
          border: `1.5px solid ${colors.gray.primary}`,

          '&.Mui-error': {
            borderColor: colors.red.primary,
          },

          '&::before, &::after': {
            borderBottom: 'none !important',
          },

          [`& .${pickersSectionListClasses.root}`]: {
            padding: '22px 12px 7px',
          },

          '& .MuiInputAdornment-root': {
            margin: 0,
            marginRight: '4px',
          },

          [`&.withoutLabel .${pickersSectionListClasses.root}`]: {
            padding: '8.5px 12px',
          },

          '&:hover:not(.Mui-focused)': {
            borderColor: colors.blue.primary,
            background: colors.white.primary,
          },

          [`&.${pickersFilledInputClasses.focused}, &.${pickersFilledInputClasses.focused}:hover`]:
            {
              borderColor: colors.blue.primary,
              background: colors.white.primary,
            },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiInputAdornment-positionEnd': {
            transform: 'rotate(180deg)',
          },
        },
        paper: {
          margin: '8px 0',
          boxShadow: 'none',
          borderRadius: '16px',
          border: `1.5px solid ${colors.blue.primary}`,
          '.MuiAutocomplete-listbox': {
            padding: 0,
          },
          '.MuiAutocomplete-noOptions': {
            padding: '8px 12px',
          },
          '.MuiAutocomplete-listbox, .MuiAutocomplete-noOptions': {
            width: '100%',
            borderRadius: '16px',
            '.MuiAutocomplete-option': optionStyles,
          },
        },
        inputRoot: {
          height: 'auto',
          padding: '0 !important',
          background: colors.white.primary,
          '&:hover, &.Mui-focused': {
            background: colors.white.primary,
          },
          '.MuiInputAdornment-positionEnd': {
            right: 12,
            position: 'absolute',
            top: 'calc(50% - 10px)',
          },
          '.MuiFilledInput-input': {
            borderRadius: 7,
            cursor: 'pointer',
            padding: '8.5px 12px',
            paddingRight: '32px !important',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          padding: filledInputPadding,
        },
        root: {
          border: '1.5px solid',
          color: colors.black.primary,
          borderColor: colors.gray.primary,
          backgroundColor: colors.white.primary,

          '&:hover, &.Mui-focused': {
            backgroundColor: colors.white.primary,
          },

          '&.MuiInputBase-root': {
            overflow: 'hidden',
            borderRadius: '12px',
            '& textarea': {
              padding: '0',
            },
            '&::after': {
              borderBottom: 'none',
            },
            '&::before': {
              borderBottom: 'none',
            },
            '&:hover::before': {
              borderBottom: 'none',
            },
            '&.selectInput': {
              paddingTop: '0 !important',
            },
            '&.Mui-focused::after': {
              borderBottom: 'none',
            },
            '&.Mui-error': {
              borderColor: colors.red.primary,
            },
            '& input': {
              height: '19px',
              paddingTop: '22px',
              paddingBottom: '8px',
            },
            '&:hover': {
              borderColor: colors.gray.primary,
              input: {
                backgroundColor: colors.white.hover,
              },
            },
            '&.MuiFilledInput-multiline': {
              paddingTop: '21px',

              '&.withoutLabel': {
                paddingTop: '7.5px',
              },
            },
            '&.Mui-focused': {
              borderColor: colors.blue.primary,
              input: {
                backgroundColor: colors.white.primary,
              },
            },
            '& input:-webkit-autofill': {
              WebkitTextFillColor: colors.black.primary,
              WebkitBoxShadow: `0 0 0 1000px ${colors.white.primary} inset`,
            },
            '&.withoutLabel': {
              minHeight: '38px',
              '& input': {
                height: '35px',
                paddingTop: '0',
                paddingBottom: '0',
              },
            },

            '&.Mui-disabled': {
              borderColor: colors.gray.primary,
              '&:hover': {
                borderColor: colors.gray.primary,
              },
              '.MuiOutlinedInput-input': {
                borderColor: colors.gray.primary,
              },
              '&:hover .MuiOutlinedInput-input': {
                borderColor: colors.gray.primary,
              },
            },
          },
        },
      },
    },
  },
});

export { colors, gradients };
export default theme;
